import * as React from "react";
import {
  CellContext,
  createColumnHelper
} from "@tanstack/react-table";
import styled from "styled-components";

import { Text } from "src/library/components/Text";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import {
  Invoice,
  InvoiceStatus
} from "src/core/accounting/api/accounting.api";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import AccountingStatusBadge from "src/core/accounting/components/AccountingStatusBadge";
import { formatDropName } from "src/core/drops/utils/format-drop-name";
import { formatCustomerName } from "src/core/customers/utils/format-customer-name";

export interface IAdminInvoiceTableColumns {
  actionButton: (info: CellContext<Invoice, unknown>) => React.ReactNode;
  terminology: ITerminology;
}

export const adminInvoiceTableColumns = (params: IAdminInvoiceTableColumns) => {
  const customColumnHelper = customCreateColumnHelper<Invoice>();
  const columnHelper = createColumnHelper<Invoice>();

  // Invoice # column with document icon
  const invoiceNumberColumn = customColumnHelper.standardTextColumn(columnHelper, "invoiceNumber", {
    header: "Invoice #",
    cell: ({ getValue }) => {
      const invoiceNumber = getValue() as string;

      return (
        <InvoiceNumberCell>
          <Icon
            icon={Icons.NOTES}
            size={16}
            $fillColor="primaryLight"
          />

          <Text>
            {`Invoice # ${invoiceNumber}`}
          </Text>
        </InvoiceNumberCell>
      );
    }
  });

  // Customer column (admin only) - use accessor for filtering support
  const customerColumn = customColumnHelper.standardTextColumn(columnHelper, "customer", {
    enableSorting: false,
    enableColumnFilter: true,
    cell: info => (
      <Text>
        {info.row.original.customer ? formatCustomerName(info.row.original.customer) : "-"}
      </Text>
    ),
    meta: {
      style: { align: "left" },
      filter: {
        filterType: "string",
        filterOperation: "none",
        minLength: 3
      }
    }
  }
  );

  // Branch column (admin only) - use accessor for filtering support
  const branchColumn = customColumnHelper.standardTextColumn(columnHelper, "branch", {
    header: params.terminology.drop.singular.text,
    enableSorting: false,
    enableColumnFilter: true,
    cell: (info: CellContext<Invoice, unknown>) => (
      <Text>
        {info.row.original.branch ? formatDropName(info.row.original.branch) : "-"}
      </Text>
    ),
    meta: {
      style: { align: "left" },
      filter: {
        filterType: "string",
        filterOperation: "none",
        minLength: 3
      }
    }
  });

  // Payment Due date column
  const paymentDueColumn = customColumnHelper.dateColumn(columnHelper, "dueDate", {
    header: "Payment Due",
    enableSorting: true
  });

  // Total amount column
  const totalColumn = customColumnHelper.priceColumn(columnHelper, "totalAmount", { header: "Invoice Total" }, "GBP");
  const outstandingBalanceColumn = customColumnHelper.priceColumn(columnHelper, "outstandingAmount", { header: "Outstanding Amount" }, "GBP");

  // Status badge column
  const statusColumn = customColumnHelper.standardTextColumn(columnHelper, "status", {
    header: "Status",
    cell: ({ getValue }) => {
      const status = getValue() as InvoiceStatus;

      return (
        <AccountingStatusBadge
          variant="invoice"
          status={status}
          compact
        />
      );
    }
  });

  // Actions column
  const actionsColumn = customColumnHelper.actionColumn(columnHelper, params.actionButton, { size: 120 });

  // Build columns array - admin view includes customer and branch
  const columns = [
    invoiceNumberColumn,
    customerColumn,
    branchColumn,
    paymentDueColumn,
    totalColumn,
    outstandingBalanceColumn,
    statusColumn,
    actionsColumn
  ];

  return columns;
};

const InvoiceNumberCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
